import { useCallback, useRef } from "react";

const useStickyTransform = () => {
  const rafIdRef = useRef<number | null>(null);
  const needsTransformRef = useRef(false);
  const offsetTopRef = useRef(0);

  const applyTransform = useCallback((element: HTMLElement | null) => {
    if (element && needsTransformRef.current) {
      const offset = offsetTopRef.current;
      const isTransformed = offset !== 0;

      element.dataset.isTransformed = String(isTransformed);
      // Clear the transform when idle instead of writing `translateY(0)`: keeping one promotes the
      // row to its own compositor layer, which makes Firefox flash the grey backing as rows
      // pin/unpin while scrolling.
      element.style.transform = isTransformed ? `translateY(${offset}px)` : "";
      needsTransformRef.current = false;
    }
    rafIdRef.current = null;
  }, []);

  const scheduleUpdate = useCallback(
    (element: HTMLElement | null) => {
      if (rafIdRef.current === null) {
        rafIdRef.current = requestAnimationFrame(() => applyTransform(element));
      }
    },
    [applyTransform]
  );

  const updateTransform = useCallback(
    (element: HTMLElement | null, scrollTop: number, nextAfterStickyPosition?: number) => {
      if (!nextAfterStickyPosition) return;

      const offset = nextAfterStickyPosition - scrollTop;
      offsetTopRef.current = offset < 0 ? offset : 0;
      needsTransformRef.current = true;
      scheduleUpdate(element);
    },
    [scheduleUpdate]
  );

  const cleanup = useCallback(() => {
    if (rafIdRef.current !== null) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    }
  }, []);

  // Without this, a row that stops being pinned keeps its last `translateY` and stays shifted out
  // of view, leaving a gap where it should render.
  const reset = useCallback((element: HTMLElement | null) => {
    offsetTopRef.current = 0;
    needsTransformRef.current = false;

    if (element) {
      element.style.transform = "";
      delete element.dataset.isTransformed;
    }
  }, []);

  return {
    updateTransform,
    cleanup,
    reset,
  };
};

export default useStickyTransform;
